import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  formatDurationSecondsCompact,
  NO_VALUE_DISPLAY,
  numericValueWithCommas,
} from "@ryde/shared";

import { StationTimingDelta } from "./station-timing-preview.delta";
import { IStationTimingDayRow } from "./station-timing-preview.models";
import {
  computeStationTimingDelta,
  getStationTimingDayNameKey,
} from "./station-timing-preview.utils";

interface IStationTimingPreviewDayRowProps {
  dayRow: IStationTimingDayRow;
  /** Planned time of the leg the day belongs to; the day's own comparison is made against it. */
  plannedSeconds: number | undefined;
  /** Repeated for screen readers only: a day row is read far away from the leg it belongs to. */
  legName: string;
}

export const StationTimingPreviewDayRow: FC<
  IStationTimingPreviewDayRowProps
> = ({ dayRow, plannedSeconds, legName }) => {
  const { t } = useTranslation();
  const dayNameKey = getStationTimingDayNameKey(dayRow.dayOfWeek);
  const plannedLabel =
    formatDurationSecondsCompact(plannedSeconds) || NO_VALUE_DISPLAY;
  // The median is the figure the day line leads with, so it is also the one it compares to the plan.
  const dayDelta = computeStationTimingDelta({
    plannedSeconds,
    measuredSeconds: dayRow.medianSeconds,
  });

  return (
    <tr className="station-timing__day-row">
      <th scope="row" className="station-timing__day">
        <span className="station-timing__day-name">
          {dayNameKey ? t(dayNameKey) : NO_VALUE_DISPLAY}
        </span>
        <span className="station-timing__visually-hidden">
          {t("stationTiming.DAY_ROW_CONTEXT", { leg: legName })}
        </span>
      </th>
      <td className="station-timing__planned station-timing__numeric">
        {plannedLabel}
      </td>
      <td className="station-timing__measure">
        <span className="station-timing__day-measure">
          <span className="station-timing__day-median station-timing__numeric">
            {formatDurationSecondsCompact(dayRow.medianSeconds) ||
              NO_VALUE_DISPLAY}
          </span>
          <span className="station-timing__day-hint">
            <span>{t("stationTiming.OPTION_MEDIAN")}</span>
            {/* Separator only; the two figures already name themselves. */}
            <span aria-hidden="true">&#183;</span>
            <span className="station-timing__numeric">
              {formatDurationSecondsCompact(dayRow.avgSeconds) ||
                NO_VALUE_DISPLAY}
            </span>
            <span>{t("stationTiming.OPTION_AVERAGE")}</span>
          </span>
        </span>
      </td>
      <td className="station-timing__delta-cell">
        <StationTimingDelta delta={dayDelta} isCompact />
      </td>
      <td className="station-timing__based-on">
        <span className="station-timing__numeric">
          {numericValueWithCommas(dayRow.datapoints)}
        </span>
        <span className="station-timing__based-on-unit">
          {t("stationTiming.RIDES_UNIT")}
        </span>
      </td>
    </tr>
  );
};
